import { useRef, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// 나중에 삭제 예정임
export const useModalFixed = ({
  isShow,
  needBackgroundFixed,
  fixedTime = 0,
  overflowHidden = false,
  content,
}: {
  isShow: boolean;
  needBackgroundFixed?: boolean;
  fixedTime?: number;
  overflowHidden?: boolean;
  content?: HTMLElement;
}) => {
  // iOS
  const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const location = useLocation();

  const documentElementRef = useRef(document.documentElement);

  // 모달 참조
  const modalWrapRef = useRef<HTMLDivElement>(null);

  // 모달 컨텐츠 참조
  const modalContentRef = useRef<HTMLDivElement>(null);

  // 마지막 스크롤 위치
  const lastScrollTop = useRef<number>(0);

  // 이미 고정된 상태인지 여부
  const alreadyFixed = useRef<null | boolean>(null);

  // 스크롤 방지 함수
  const preventScroll = useCallback((e) => {
    const condition1 = !modalContentRef?.current
      ? !modalWrapRef.current.contains(e.target)
      : !modalContentRef.current?.contains(e.target);
    const condition2 = e.target.closest(".prevent-scroll") && e.target.tagName !== "TEXTAREA";
    const condition3 =
      e.target.closest(".prevent-scroll") && e.target.scrollHeight > e.target.clientHeight;

    if (condition2) {
      e.stopPropagation();
      e.preventDefault();
    } else {
      if (condition3) {
        e.stopPropagation();
      } else {
        if (condition1) {
          e.stopPropagation();
          e.preventDefault();
        }
      }
    }
  }, []);

  // 스크롤 방지 핸들러
  const preventScrollHandler = useCallback(() => {
    // 'wheel' 이벤트를 사용하여 스크롤 감지 후 방지
    document.documentElement.classList.add("for-modal-fixed");
    document.documentElement.classList.add("hide-scroll");
    document.body.addEventListener("wheel", preventScroll, { passive: false });
    document.body.addEventListener("touchmove", preventScroll, { passive: false });
  }, []);

  // 스크롤 방지 해제 핸들러
  const removePreventScrollHandler = useCallback(() => {
    document.documentElement.classList.remove("for-modal-fixed");
    document.documentElement.classList.remove("hide-scroll");
    document.body.removeEventListener("wheel", preventScroll);
    document.body.removeEventListener("touchmove", preventScroll);
  }, []);

  // 뒷배경 오버플로 히든처리
  const hideBackgroundOverflowHandler = useCallback(() => {
    if (isiOS) return;
    if (!overflowHidden) return;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  }, []);

  // 뒷배경 오버플로 해제 처리
  const showBackgroundOverflowHandler = useCallback(() => {
    if (isiOS) return;
    if (!overflowHidden) return;
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  }, []);

  // 타임 아웃 처리
  const timeoutBlockedHandler = useCallback(() => {
    if (needBackgroundFixed) {
      if (document.documentElement.classList?.contains("for-modal-fixed")) {
        alreadyFixed.current = true;
      } else {
        alreadyFixed.current = false;
      }
      if (overflowHidden) {
        let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        lastScrollTop.current = scrollTop;
        hideBackgroundOverflowHandler();
      }
      // 'wheel' 이벤트를 사용하여 스크롤 감지 후 방지
      if (alreadyFixed.current === false) {
        preventScrollHandler();
      }
    }
  }, []);

  // 스크롤 방지 해제 핸들러
  const unblockedHandler = useCallback(() => {
    if (
      needBackgroundFixed &&
      alreadyFixed.current === false &&
      document.documentElement.classList.contains("for-modal-fixed")
    ) {
      if (overflowHidden) {
        showBackgroundOverflowHandler();
      }
      removePreventScrollHandler();
      if (overflowHidden && lastScrollTop.current > 0) {
        window.scrollTo(0, lastScrollTop.current);
      }
    } else {
      showRef.current = false;
      hideRef.current = true;
    }
    alreadyFixed.current = null;
  }, []);

  const timerIdRef = useRef<NodeJS.Timeout | null>(null);
  const timerIdRef2 = useRef<NodeJS.Timeout | null>(null);

  const [lastLocation, setLastLocation] = useState(null);

  const hideRef = useRef(false);
  const showRef = useRef(false);

  // 모달 표시 처리
  useEffect(() => {
    documentElementRef.current = document.documentElement;

    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
    }
    if (timerIdRef2.current) {
      clearTimeout(timerIdRef2.current);
    }

    if (isShow) {
      hideRef.current = false;
      showRef.current = true;

      if (fixedTime) {
        timerIdRef.current = setTimeout(() => {
          timeoutBlockedHandler();
        }, fixedTime);
      } else {
        timeoutBlockedHandler();
      }
      setLastLocation(location);
    } else {
      if (fixedTime) {
        unblockedHandler();
        timerIdRef.current = setTimeout(() => {
          hideRef.current = true;
          showRef.current = false;
        }, fixedTime);
      } else {
        unblockedHandler();
        hideRef.current = true;
        showRef.current = false;
      }
    }

    return () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
      }
      if (timerIdRef2.current) {
        clearTimeout(timerIdRef2.current);
      }
      showRef.current = false;
      hideRef.current = true;
      if (overflowHidden) {
        showBackgroundOverflowHandler();
      }
      removePreventScrollHandler();
      if (overflowHidden) {
        if (lastScrollTop.current > 0) {
          window.scrollTo(0, lastScrollTop.current);
        }
      }
    };
  }, [isShow]);

  return {
    modalWrapRef,
    modalContentRef,
    HIDE: hideRef.current,
    SHOW: showRef.current,
  };
};
